// Central icon provider configuration for ng-icons
// Registers all Tabler icons, Simple Icons, and custom SVGs used across the app

import { Provider } from '@angular/core';
import { provideIcons } from '@ng-icons/core';

// Tabler Icons (outline style, 1.5px stroke)
import {
  tablerActivity,
  tablerAdjustments,
  tablerHome,
  tablerNews,
  tablerRobot,
  tablerFileUpload,
  tablerUsersGroup,
  tablerBuildingCommunity,
  tablerBuildingSkyscraper,
  tablerAlertCircle,
  tablerAlertTriangle,
  tablerArchive,
  tablerArrowBackUp,
  tablerArrowDown,
  tablerArrowLeft,
  tablerArrowRight,
  tablerArrowUp,
  tablerArrowsExchange,
  tablerArrowsMaximize,
  tablerArrowsSort,
  tablerArticle,
  tablerBan,
  tablerBell,
  tablerBellOff,
  tablerBolt,
  tablerBrain,
  tablerBriefcase,
  tablerBuilding,
  tablerCalendar,
  tablerCalendarOff,
  tablerCamera,
  tablerCaretDown,
  tablerCash,
  tablerCertificate,
  tablerChartBar,
  tablerTrendingUp,
  tablerCheck,
  tablerChevronDown,
  tablerChevronLeft,
  tablerChevronRight,
  tablerChevronUp,
  tablerCircle,
  tablerCircleArrowDown,
  tablerCircleArrowUp,
  tablerCircleCheck,
  tablerCircleCheckFilled,
  tablerCircleChevronDown,
  tablerCircleChevronUp,
  tablerCircleFilled,
  tablerCircleMinus,
  tablerCirclePlus,
  tablerCircleX,
  tablerCircleXFilled,
  tablerClipboard,
  tablerClipboardCheck,
  tablerClipboardList,
  tablerClipboardText,
  tablerClock,
  tablerCloudUpload,
  tablerCoin,
  tablerCopy,
  tablerCreditCard,
  tablerCrown,
  tablerCurrencyDollar,
  tablerDeviceFloppy,
  tablerDots,
  tablerDotsVertical,
  tablerDownload,
  tablerEdit,
  tablerEraser,
  tablerExternalLink,
  tablerEye,
  tablerEyeOff,
  tablerFeather,
  tablerFile,
  tablerFileCheck,
  tablerFileDescription,
  tablerFileDownload,
  tablerFileInvoice,
  tablerFileSpreadsheet,
  tablerFileText,
  tablerFilter,
  tablerFlag,
  tablerFlame,
  tablerFolder,
  tablerGripVertical,
  tablerHandClick,
  tablerHeart,
  tablerHeartHandshake,
  tablerHelp,
  tablerHistory,
  tablerHourglass,
  tablerId,
  tablerInbox,
  tablerInfoCircle,
  tablerLayoutGrid,
  tablerLink,
  tablerListCheck,
  tablerLoader2,
  tablerLock,
  tablerLogout,
  tablerMail,
  tablerMailOff,
  tablerMailForward,
  tablerMapPin,
  tablerMenu2,
  tablerMessage,
  tablerMessages,
  tablerMusic,
  tablerNetwork,
  tablerPaperclip,
  tablerPdf,
  tablerPencil,
  tablerPhone,
  tablerPlus,
  tablerPointer,
  tablerQuote,
  tablerRefresh,
  tablerRocket,
  tablerSchool,
  tablerSearch,
  tablerSearchOff,
  tablerSend,
  tablerSettings,
  tablerShare,
  tablerShield,
  tablerShieldLock,
  tablerSitemap,
  tablerSparkles,
  tablerSquare,
  tablerSquareCheck,
  tablerStack2,
  tablerStar,
  tablerThumbDown,
  tablerThumbUp,
  tablerTrash,
  tablerUpload,
  tablerUser,
  tablerUserCheck,
  tablerUserCircle,
  tablerUserPlus,
  tablerUserSearch,
  tablerUsers,
  tablerVideo,
  tablerWand,
  tablerWorld,
  tablerWritingSign,
  tablerX,
  tablerZoomIn,
  tablerZoomOut,
  tablerZoomQuestion,
} from '@ng-icons/tabler-icons';

// Simple Icons (brand/social icons)
import {
  simpleFacebook,
  simpleInstagram,
  simpleLinkedin,
  simpleTiktok,
  simpleTwitter,
  simpleYoutube,
} from '@ng-icons/simple-icons';

// Custom SVG icons (business-specific, extracted from svg-templates)
import {
  customAiProcessing,
  customAnalytics,
  customApplicants,
  customApplied,
  customApply,
  customBilling,
  customBusiness,
  customCandidatePosted,
  customCandidateSubmissions,
  customCandidates,
  customCandidatesWhite,
  customCommunity,
  customCompany,
  customCompanyContacts,
  customCompanyFinancials,
  customCompanyPortfolio,
  customCompanyProfile,
  customCompanyServices,
  customContacts,
  customConvert,
  customCreateJob,
  customCredit,
  customDetails,
  customDone,
  customDoneMedium,
  customDoneSmall,
  customDoubt,
  customEmpty,
  customEmpty1,
  customEngage,
  customExcel,
  customJobApplicant,
  customJobInviteReceived,
  customJobInviteSent,
  customJobPosted,
  customNetwork,
  customNetwork1,
  customNetwork2,
  customNetworkIllustration,
  customNotFound,
  customNotFound100,
  customOptions,
  customOutbox,
  customPaperPlane,
  customParty,
  customPosts,
  customPremium,
  customReferral,
  customResumeDownload,
  customResumeRequest,
  customResumeRequestAnalytics,
  customResumeRequestReceived,
  customSearchApp,
  customSearchCross,
  customSearchCrossXl,
  customSearchJob,
  customSelect,
  customSignPen,
  customSlots,
  customSubscription,
  customSuggested,
  customSuitcase,
  customSuitcaseBlue,
  customSuitcaseDark,
  customSuitcasePlus,
  customSuitcaseSmall,
  customSuitcaseWhite,
  customTeam,
  customTeamColor,
  customWrite,
  customX,
} from './custom-icons';

/**
 * All icons used in the app, registered for ng-icons.
 */
export const APP_ICONS: Record<string, string> = {
  // Tabler Icons
  tablerActivity,
  tablerAdjustments,
  tablerHome,
  tablerNews,
  tablerRobot,
  tablerFileUpload,
  tablerUsersGroup,
  tablerBuildingCommunity,
  tablerBuildingSkyscraper,
  tablerAlertCircle,
  tablerAlertTriangle,
  tablerArchive,
  tablerArrowBackUp,
  tablerArrowDown,
  tablerArrowLeft,
  tablerArrowRight,
  tablerArrowUp,
  tablerArrowsExchange,
  tablerArrowsMaximize,
  tablerArrowsSort,
  tablerArticle,
  tablerBan,
  tablerBell,
  tablerBellOff,
  tablerBolt,
  tablerBrain,
  tablerBriefcase,
  tablerBuilding,
  tablerCalendar,
  tablerCalendarOff,
  tablerCamera,
  tablerCaretDown,
  tablerCash,
  tablerCertificate,
  tablerChartBar,
  tablerTrendingUp,
  tablerCheck,
  tablerChevronDown,
  tablerChevronLeft,
  tablerChevronRight,
  tablerChevronUp,
  tablerCircle,
  tablerCircleArrowDown,
  tablerCircleArrowUp,
  tablerCircleCheck,
  tablerCircleCheckFilled,
  tablerCircleChevronDown,
  tablerCircleChevronUp,
  tablerCircleFilled,
  tablerCircleMinus,
  tablerCirclePlus,
  tablerCircleX,
  tablerCircleXFilled,
  tablerClipboard,
  tablerClipboardCheck,
  tablerClipboardList,
  tablerClipboardText,
  tablerClock,
  tablerCloudUpload,
  tablerCoin,
  tablerCopy,
  tablerCreditCard,
  tablerCrown,
  tablerCurrencyDollar,
  tablerDeviceFloppy,
  tablerDots,
  tablerDotsVertical,
  tablerDownload,
  tablerEdit,
  tablerEraser,
  tablerExternalLink,
  tablerEye,
  tablerEyeOff,
  tablerFeather,
  tablerFile,
  tablerFileCheck,
  tablerFileDescription,
  tablerFileDownload,
  tablerFileInvoice,
  tablerFileSpreadsheet,
  tablerFileText,
  tablerFilter,
  tablerFlag,
  tablerFlame,
  tablerFolder,
  tablerGripVertical,
  tablerHandClick,
  tablerHeart,
  tablerHeartHandshake,
  tablerHelp,
  tablerHistory,
  tablerHourglass,
  tablerId,
  tablerInbox,
  tablerInfoCircle,
  tablerLayoutGrid,
  tablerLink,
  tablerListCheck,
  tablerLoader2,
  tablerLock,
  tablerLogout,
  tablerMail,
  tablerMailOff,
  tablerMailForward,
  tablerMapPin,
  tablerMenu2,
  tablerMessage,
  tablerMessages,
  tablerMusic,
  tablerNetwork,
  tablerPaperclip,
  tablerPdf,
  tablerPencil,
  tablerPhone,
  tablerPlus,
  tablerPointer,
  tablerQuote,
  tablerRefresh,
  tablerRocket,
  tablerSchool,
  tablerSearch,
  tablerSearchOff,
  tablerSend,
  tablerSettings,
  tablerShare,
  tablerShield,
  tablerShieldLock,
  tablerSitemap,
  tablerSparkles,
  tablerSquare,
  tablerSquareCheck,
  tablerStack2,
  tablerStar,
  tablerThumbDown,
  tablerThumbUp,
  tablerTrash,
  tablerUpload,
  tablerUser,
  tablerUserCheck,
  tablerUserCircle,
  tablerUserPlus,
  tablerUserSearch,
  tablerUsers,
  tablerVideo,
  tablerWand,
  tablerWorld,
  tablerWritingSign,
  tablerX,
  tablerZoomIn,
  tablerZoomOut,
  tablerZoomQuestion,

  // Simple Icons (brand/social)
  simpleFacebook,
  simpleInstagram,
  simpleLinkedin,
  simpleTiktok,
  simpleTwitter,
  simpleYoutube,

  // Custom SVG Icons
  customAiProcessing,
  customAnalytics,
  customApplicants,
  customApplied,
  customApply,
  customBilling,
  customBusiness,
  customCandidatePosted,
  customCandidateSubmissions,
  customCandidates,
  customCandidatesWhite,
  customCommunity,
  customCompany,
  customCompanyContacts,
  customCompanyFinancials,
  customCompanyPortfolio,
  customCompanyProfile,
  customCompanyServices,
  customContacts,
  customConvert,
  customCreateJob,
  customCredit,
  customDetails,
  customDone,
  customDoneMedium,
  customDoneSmall,
  customDoubt,
  customEmpty,
  customEmpty1,
  customEngage,
  customExcel,
  customJobApplicant,
  customJobInviteReceived,
  customJobInviteSent,
  customJobPosted,
  customNetwork,
  customNetwork1,
  customNetwork2,
  customNetworkIllustration,
  customNotFound,
  customNotFound100,
  customOptions,
  customOutbox,
  customPaperPlane,
  customParty,
  customPosts,
  customPremium,
  customReferral,
  customResumeDownload,
  customResumeRequest,
  customResumeRequestAnalytics,
  customResumeRequestReceived,
  customSearchApp,
  customSearchCross,
  customSearchCrossXl,
  customSearchJob,
  customSelect,
  customSignPen,
  customSlots,
  customSubscription,
  customSuggested,
  customSuitcase,
  customSuitcaseBlue,
  customSuitcaseDark,
  customSuitcasePlus,
  customSuitcaseSmall,
  customSuitcaseWhite,
  customTeam,
  customTeamColor,
  customWrite,
  customX,
};

/**
 * Provider for all app icons. Add to app-level providers.
 */
export function provideAppIcons(): Provider[] {
  return provideIcons(APP_ICONS);
}
